import dayjs, { Dayjs } from 'dayjs';
import { DateOptions } from 'store/api/enums';

const displayDateFormat = 'DD/MM/YYYY';

export type ApiDateRange = {
  from?: string;
  to?: string;
};

const toApiDate = (value: Dayjs): string => value.format('YYYY-MM-DD');

/** Dayjs range for ScopeTable-style date presets (display format). */
export const getDayjsRangeForDateOption = (
  value: DateOptions
): [Dayjs, Dayjs] | null => {
  if (value === DateOptions.ALL) return null;

  const today = dayjs().format(displayDateFormat);
  let start = '';
  let end = '';

  switch (value) {
    case DateOptions.TODAY:
      return [dayjs(today, displayDateFormat), dayjs(today, displayDateFormat)];
    case DateOptions.THIS_WEEK:
      start = dayjs().startOf('week').format(displayDateFormat);
      end = dayjs().endOf('week').format(displayDateFormat);
      break;
    case DateOptions.THIS_MONTH:
      start = dayjs().startOf('month').format(displayDateFormat);
      end = dayjs().endOf('month').format(displayDateFormat);
      break;
    case DateOptions.THIS_QUARTER:
      start = dayjs().startOf('quarter').format(displayDateFormat);
      end = dayjs().endOf('quarter').format(displayDateFormat);
      break;
    case DateOptions.NEXT_WEEK:
      start = dayjs().add(1, 'week').startOf('week').format(displayDateFormat);
      end = dayjs().add(1, 'week').endOf('week').format(displayDateFormat);
      break;
    case DateOptions.NEXT_MONTH:
      start = dayjs().add(1, 'month').startOf('month').format(displayDateFormat);
      end = dayjs().add(1, 'month').endOf('month').format(displayDateFormat);
      break;
    case DateOptions.NEXT_QUARTER:
      start = dayjs().add(1, 'quarter').startOf('quarter').format(displayDateFormat);
      end = dayjs().add(1, 'quarter').endOf('quarter').format(displayDateFormat);
      break;
    case DateOptions.THIS_YEAR:
      start = dayjs().startOf('year').format(displayDateFormat);
      end = dayjs().endOf('year').format(displayDateFormat);
      break;
    case DateOptions.LAST_WEEK:
      start = dayjs().subtract(1, 'week').startOf('week').format(displayDateFormat);
      end = dayjs().subtract(1, 'week').endOf('week').format(displayDateFormat);
      break;
    case DateOptions.LAST_MONTH:
      start = dayjs().subtract(1, 'month').startOf('month').format(displayDateFormat);
      end = dayjs().subtract(1, 'month').endOf('month').format(displayDateFormat);
      break;
    case DateOptions.LAST_YEAR:
      start = dayjs().subtract(1, 'year').startOf('year').format(displayDateFormat);
      end = dayjs().subtract(1, 'year').endOf('year').format(displayDateFormat);
      break;
    default:
      return null;
  }

  return [dayjs(start, displayDateFormat), dayjs(end, displayDateFormat)];
};

export const getApiDateRangeForDateOption = (
  value: DateOptions
): ApiDateRange => {
  const range = getDayjsRangeForDateOption(value);
  if (!range) return {};
  return { from: toApiDate(range[0]), to: toApiDate(range[1]) };
};

export const getApiDateRangeFromDayjs = (
  range: [Dayjs | null, Dayjs | null]
): ApiDateRange => {
  const from = range[0]?.format('YYYY-MM-DD');
  const to = range[1]?.format('YYYY-MM-DD');
  return { from, to };
};
